import { ClerkProvider } from "@clerk/nextjs";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { PropsWithChildren } from "react";

import { IS_PROD } from "~/env";
import { ShelfCycleProvider } from "~/ui";

import { ActionCenterProvider } from "./actionCenterProvider";
import { AssistantConversationProvider } from "./assistantConversationProvider";
import { AuthorizationProvider } from "./authorizationProvider";
import { FeatureFlagProvider } from "./featureFlagProvider";
import { OrgProvider } from "./orgProvider";
import { UIProvider } from "./UIProvider";
import { UserProvider } from "./userProvider";

interface AppProviderProps {
  pageProps: AppProps["pageProps"];
}

/**
 * Compound provider for centralizing all necessary providers used within
 * the ShelfCycle app bounds
 */
export const AppProvider = (props: PropsWithChildren<AppProviderProps>) => {
  const { pathname } = useRouter();
  const providerExtraSettings = {
    // Clerk's telemetry beacon posts to clerk-telemetry.com, which our CSP `connect-src` does not allow.
    telemetry: { disabled: true },
    ...(IS_PROD
      ? {
          signInUrl: "/sign-in",
          signUpUrl: "/sign-up",
          allowedRedirectOrigins: ["https://shelfcycle.net"],
        }
      : {}),
  };

  if (pathname.startsWith("/switch-org")) {
    return (
      <NuqsAdapter>
        <ClerkProvider {...props.pageProps} {...providerExtraSettings}>
          <ShelfCycleProvider>
            <UIProvider>{props.children}</UIProvider>
          </ShelfCycleProvider>
        </ClerkProvider>
      </NuqsAdapter>
    );
  }

  return (
    <NuqsAdapter>
      <ClerkProvider {...props.pageProps} {...providerExtraSettings}>
        {/* Above the loading providers: their AppLoader renders ~/ui, which Mantine 7 cannot render without a provider (SC-2703). */}
        <ShelfCycleProvider>
          <UserProvider>
            <OrgProvider>
              <FeatureFlagProvider>
                <AuthorizationProvider>
                  <AssistantConversationProvider>
                    <UIProvider>
                      <ActionCenterProvider>{props.children}</ActionCenterProvider>
                    </UIProvider>
                  </AssistantConversationProvider>
                </AuthorizationProvider>
              </FeatureFlagProvider>
            </OrgProvider>
          </UserProvider>
        </ShelfCycleProvider>
      </ClerkProvider>
    </NuqsAdapter>
  );
};
